import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, CalendarPlus, BellPlus, StickyNote, CalendarDays, PhoneCall, Sun, Moon,
  Settings, LogOut, Loader2, Check, X, Plane, Clock, Phone, MessageSquare, Plus,
  Gauge, ChevronRight, Trash2, MapPin, Coins,
} from "lucide-react";
import { api, clearToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import "@/pa/pa-theme.css";

const WELCOME_BG = "https://images.pexels.com/photos/6699772/pexels-photo-6699772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRAVEL_IMG = "https://images.pexels.com/photos/15713593/pexels-photo-15713593.jpeg";
const EVENT_IMG = "https://images.pexels.com/photos/9534914/pexels-photo-9534914.jpeg";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }); } catch { return iso; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

export default function PADashboard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem("pa_theme") || "dark");
  const [modal, setModal] = useState(null); // 'event'|'reminder'|'note'|'booking'|'calendar'|'call'|'settings'|'minutes'|'travel'

  const load = useCallback(async () => {
    const { data } = await api.get("/pa/dashboard");
    setData(data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => { localStorage.setItem("pa_theme", theme); }, [theme]);

  const logout = () => { clearToken(); refresh(); navigate("/personal"); };

  if (!data) return <div className={`theme-pa ${theme === "light" ? "pa-light" : ""} min-h-screen flex items-center justify-center`}><Loader2 className="h-6 w-6 animate-spin pa-gold" /></div>;

  const sub = data.subscription || {};
  const used = sub.minutes_used || 0, limit = sub.minutes_limit || 0;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const name = data.assistant_name || "Ava";
  const first = (data.owner_name || "").split(" ")[0] || "there";

  const actions = [
    { key: "event", label: "Add Event", icon: CalendarPlus },
    { key: "reminder", label: "Create Reminder", icon: BellPlus },
    { key: "note", label: "Add Note", icon: StickyNote },
    { key: "calendar", label: "View Calendar", icon: CalendarDays },
    { key: "call", label: "Call Contact", icon: PhoneCall },
  ];

  const stagger = (i) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.05, duration: 0.5 } });

  return (
    <div className={`theme-pa ${theme === "light" ? "pa-light" : ""}`} data-testid="pa-dashboard">
      <div className="pa-vignette min-h-screen px-4 sm:px-8 py-6 max-w-7xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 pa-gold" strokeWidth={1.5} />
            <span className="pa-display text-2xl font-semibold">OperaMind <span className="pa-gold">Personal</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="pa-theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="pa-glass p-2.5 pa-hoverable" title="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4 pa-gold" strokeWidth={1.5} /> : <Moon className="h-4 w-4 pa-gold" strokeWidth={1.5} />}
            </button>
            <button data-testid="pa-settings-btn" onClick={() => setModal("settings")} className="pa-glass p-2.5 pa-hoverable" title="Assistant settings">
              <Settings className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button data-testid="pa-logout-btn" onClick={logout} className="pa-glass p-2.5 pa-hoverable" title="Sign out">
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Welcome hero */}
        <motion.div {...stagger(0)} className="pa-glass overflow-hidden relative mb-4">
          <img src={WELCOME_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.6), rgba(0,0,0,0.15))" }} />
          <div className="relative p-8 sm:p-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6" style={{ color: "#FDFBF7" }}>
            <div>
              <p className="text-sm" style={{ color: "rgba(253,251,247,0.75)" }}>{greeting()}, {first}.</p>
              {(data.todays_agenda.length + data.booking_requests.length) === 0 ? (
                <h1 className="pa-display text-4xl sm:text-5xl font-light mt-2 leading-tight">
                  Your day is <span className="pa-gold">clear</span>. Enjoy it.
                </h1>
              ) : (
                <h1 className="pa-display text-4xl sm:text-5xl font-light mt-2 leading-tight">
                  {name} has <span className="pa-gold">{data.todays_agenda.length + data.booking_requests.length}</span> things for you today.
                </h1>
              )}
              <p className="text-sm mt-2" style={{ color: "rgba(253,251,247,0.75)" }}>
                {data.booking_requests.length > 0
                  ? `${data.booking_requests.length} booking request${data.booking_requests.length === 1 ? "" : "s"} awaiting your approval.`
                  : `${name} is standing by and ready to help.`}
              </p>
            </div>
            <div className="pa-plate p-4 min-w-[190px]" style={{ background: "rgba(0,0,0,0.4)" }}>
              <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(253,251,247,0.75)" }}><Gauge className="h-4 w-4 pa-gold" strokeWidth={1.5} /> Minutes used</div>
              <p className="pa-display text-3xl mt-1">{used}<span className="text-lg" style={{ color: "rgba(253,251,247,0.6)" }}> / {limit || "∞"}</span></p>
              <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
                <div className="h-full" style={{ width: `${pct}%`, background: "var(--pa-gold)" }} />
              </div>
              <button data-testid="pa-buy-minutes-btn" onClick={() => setModal("minutes")} className="mt-3 text-xs pa-gold flex items-center gap-1 hover:underline"><Coins className="h-3.5 w-3.5" strokeWidth={1.5} /> Buy more minutes</button>
            </div>
          </div>
        </motion.div>

        {/* Quick actions */}
        <motion.div {...stagger(1)} className="pa-glass p-2 mb-4 flex flex-wrap gap-2 justify-center sm:justify-start">
          {actions.map((a) => (
            <button key={a.key} data-testid={`pa-action-${a.key}`} onClick={() => setModal(a.key)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full pa-hoverable text-sm font-medium" style={{ border: "1px solid var(--pa-glass-border)" }}>
              <a.icon className="h-4 w-4 pa-gold" strokeWidth={1.5} /> {a.label}
            </button>
          ))}
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Today's agenda */}
          <motion.div {...stagger(2)} className="pa-glass p-6 lg:col-span-8">
            <CardHead icon={CalendarDays} title="Today's Agenda" />
            {data.todays_agenda.length === 0 ? <Empty text="Nothing scheduled today. Enjoy the calm." />
              : <div className="space-y-2 mt-4">{data.todays_agenda.map((e) => <EventRow key={e.id} e={e} />)}</div>}
          </motion.div>

          {/* Booking requests */}
          <motion.div {...stagger(3)} className="pa-glass p-6 lg:col-span-4 overflow-hidden relative">
            <img src={EVENT_IMG} alt="" className="absolute top-0 right-0 w-32 h-32 object-cover opacity-20 rounded-bl-3xl" />
            <CardHead icon={Sparkles} title="Booking Requests" badge={data.booking_requests.length} />
            {data.booking_requests.length === 0 ? <Empty text="No pending requests." />
              : <div className="space-y-3 mt-4">{data.booking_requests.map((b) => (
                  <div key={b.id} data-testid={`pa-booking-${b.id}`} className="pa-plate p-3">
                    <p className="font-medium text-sm">{b.title}</p>
                    <p className="pa-muted text-xs mt-0.5">{b.requester_name}{b.date ? ` · ${b.date}` : ""}{b.fee ? ` · ${b.fee}` : ""}</p>
                    <div className="flex gap-2 mt-2">
                      <button data-testid={`pa-approve-${b.id}`} onClick={() => actionBooking(b.id, "approve", load)} className="flex-1 pa-btn-gold py-1.5 text-xs font-semibold flex items-center justify-center gap-1"><Check className="h-3.5 w-3.5" strokeWidth={2} /> Approve</button>
                      <button data-testid={`pa-decline-${b.id}`} onClick={() => actionBooking(b.id, "decline", load)} className="flex-1 py-1.5 text-xs rounded-full" style={{ border: "1px solid var(--pa-glass-border)" }}><X className="h-3.5 w-3.5 inline" strokeWidth={2} /> Decline</button>
                    </div>
                  </div>
                ))}</div>}
          </motion.div>

          {/* Recent calls */}
          <motion.div {...stagger(4)} className="pa-glass p-6 lg:col-span-8">
            <CardHead icon={Phone} title="Recent Calls" sub="with AI summaries" />
            {data.recent_calls.length === 0 ? <Empty text={`${name} hasn't taken any calls yet.`} />
              : <div className="space-y-3 mt-4">{data.recent_calls.map((c) => (
                  <div key={c.id} className="pa-plate p-4 flex items-start gap-3">
                    <div className="pa-wave flex items-end h-6 mt-1" aria-hidden>{Array.from({ length: 5 }).map((_, i) => <span key={i} style={{ animationDelay: `${i * 0.12}s` }} />)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm truncate">{c.caller_name || "Unknown caller"}</p>
                        <span className="text-xs pa-muted">{fmtDate(c.created_at)}</span>
                      </div>
                      <p className="pa-muted text-sm mt-1 leading-snug">{c.summary || "No summary available."}</p>
                    </div>
                  </div>
                ))}</div>}
          </motion.div>

          {/* Reminders */}
          <motion.div {...stagger(5)} className="pa-glass p-6 lg:col-span-4">
            <CardHead icon={BellPlus} title="Tasks & Reminders" />
            {data.reminders.length === 0 ? <Empty text="All clear. No reminders." />
              : <div className="space-y-2 mt-4">{data.reminders.map((r) => (
                  <button key={r.id} data-testid={`pa-reminder-${r.id}`} onClick={() => toggleReminder(r, load)} className="w-full flex items-center gap-3 text-left pa-plate p-3 pa-hoverable">
                    <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0" style={{ border: "1.5px solid var(--pa-gold)" }}>{r.done && <Check className="h-3 w-3 pa-gold" strokeWidth={3} />}</span>
                    <span className={`text-sm ${r.done ? "line-through opacity-50" : ""}`}>{r.text}{r.due ? <span className="pa-muted text-xs block">{fmtDate(r.due)}</span> : null}</span>
                  </button>
                ))}</div>}
          </motion.div>

          {/* Upcoming events */}
          <motion.div {...stagger(6)} className="pa-glass p-6 lg:col-span-4">
            <CardHead icon={Clock} title="Upcoming Events" />
            {data.upcoming_events.length === 0 ? <Empty text="No upcoming events." />
              : <div className="space-y-2 mt-4">{data.upcoming_events.map((e) => <EventRow key={e.id} e={e} />)}</div>}
          </motion.div>

          {/* Travel */}
          <motion.div {...stagger(7)} className="pa-glass p-6 lg:col-span-4 overflow-hidden relative">
            <img src={TRAVEL_IMG} alt="" className="absolute top-0 right-0 w-32 h-24 object-cover opacity-25 rounded-bl-3xl" />
            <div className="flex items-center justify-between">
              <CardHead icon={Plane} title="Upcoming Travel" />
              <button data-testid="pa-add-travel" onClick={() => setModal("travel")} className="pa-gold text-xs flex items-center gap-1"><Plus className="h-3.5 w-3.5" strokeWidth={2} /></button>
            </div>
            {data.upcoming_travel.length === 0 ? <Empty text="No trips planned." />
              : <div className="space-y-2 mt-4">{data.upcoming_travel.map((t) => (
                  <div key={t.id} className="pa-plate p-3">
                    <p className="font-medium text-sm">{t.title}</p>
                    <p className="pa-muted text-xs mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} />{t.origin} → {t.destination} · {fmtDate(t.depart)}</p>
                  </div>
                ))}</div>}
          </motion.div>

          {/* Messages */}
          <motion.div {...stagger(8)} className="pa-glass p-6 lg:col-span-4">
            <CardHead icon={MessageSquare} title="Messages" />
            {data.messages.length === 0 ? <Empty text="No messages." />
              : <div className="space-y-2 mt-4">{data.messages.map((m) => (
                  <div key={m.id} className="pa-plate p-3">
                    <p className="font-medium text-sm">{m.customer_name || "Someone"}</p>
                    <p className="pa-muted text-xs mt-0.5 line-clamp-2">{m.message}</p>
                  </div>
                ))}</div>}
          </motion.div>

          {/* Quick notes */}
          <motion.div {...stagger(9)} className="pa-glass p-6 lg:col-span-12">
            <div className="flex items-center justify-between">
              <CardHead icon={StickyNote} title="Quick Notes" />
              <button data-testid="pa-add-note-inline" onClick={() => setModal("note")} className="pa-gold text-xs flex items-center gap-1"><Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add</button>
            </div>
            {data.notes.length === 0 ? <Empty text="Jot down anything you need to remember." />
              : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">{data.notes.map((n) => (
                  <div key={n.id} className="pa-plate p-4 group relative">
                    <p className="text-sm whitespace-pre-wrap">{n.text}</p>
                    <button data-testid={`pa-del-note-${n.id}`} onClick={() => delNote(n.id, load)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5 pa-muted" strokeWidth={1.5} /></button>
                  </div>
                ))}</div>}
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {modal && <PAModal type={modal} data={data} onClose={() => setModal(null)} reload={load} meta={{ assistant_name: name }} />}
      </AnimatePresence>
    </div>
  );
}

/* ---- helpers ---- */
async function actionBooking(id, action, reload) {
  try { await api.post(`/pa/booking-requests/${id}/${action}`); toast.success(action === "approve" ? "Approved & added to calendar" : "Declined"); reload(); }
  catch { toast.error("Couldn't update request"); }
}
async function toggleReminder(r, reload) {
  try { await api.patch(`/pa/reminders/${r.id}`, { done: !r.done }); reload(); } catch { toast.error("Couldn't update"); }
}
async function delNote(id, reload) { try { await api.delete(`/pa/notes/${id}`); reload(); } catch {} }

const CardHead = ({ icon: Icon, title, sub, badge }) => (
  <div className="flex items-center gap-2">
    <Icon className="h-5 w-5 pa-gold" strokeWidth={1.5} />
    <h3 className="pa-display text-xl">{title}</h3>
    {sub && <span className="pa-muted text-xs">{sub}</span>}
    {badge > 0 && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--pa-gold)", color: "#0A0A0A" }}>{badge}</span>}
  </div>
);
const Empty = ({ text }) => <p className="pa-muted text-sm mt-4">{text}</p>;
const EventRow = ({ e }) => (
  <div className="flex items-center gap-3 pa-plate p-3">
    <div className="text-center min-w-[52px]">
      <p className="pa-display text-lg leading-none">{fmtTime(e.start)}</p>
      <p className="pa-muted text-[10px] mt-1">{fmtDate(e.start)}</p>
    </div>
    <div className="min-w-0">
      <p className="font-medium text-sm truncate">{e.title}</p>
      {e.location && <p className="pa-muted text-xs truncate flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} />{e.location}</p>}
    </div>
  </div>
);

/* ---- Modal (in-tree so PA theme applies) ---- */
function PAModal({ type, data, onClose, reload, meta }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const done = () => { reload(); onClose(); };

  const submit = async () => {
    setBusy(true);
    try {
      if (type === "event") { if (!f.title || !f.date) throw new Error("Title and date required"); const start = `${f.date}T${f.time || "09:00"}:00+00:00`; await api.post("/pa/events", { title: f.title, start, location: f.location || "", notes: f.notes || "" }); toast.success("Event added"); }
      else if (type === "reminder") { if (!f.text) throw new Error("Enter a reminder"); await api.post("/pa/reminders", { text: f.text, due: f.due ? `${f.due}T09:00:00+00:00` : null }); toast.success("Reminder created"); }
      else if (type === "note") { if (!f.text) throw new Error("Enter a note"); await api.post("/pa/notes", { text: f.text }); toast.success("Note saved"); }
      else if (type === "booking") { if (!f.requester_name || !f.title) throw new Error("Name and title required"); await api.post("/pa/booking-requests", f); toast.success("Booking request added"); }
      else if (type === "travel") { if (!f.title) throw new Error("Enter a trip name"); await api.post("/pa/travel", { title: f.title, origin: f.origin || "", destination: f.destination || "", depart: f.depart ? `${f.depart}T09:00:00+00:00` : "", notes: f.notes || "" }); toast.success("Trip added"); }
      else if (type === "minutes") { const r = await api.post("/pa/buy-minutes", { bundles: Number(f.bundles) || 1 }); toast.success(`Added ${r.data.added_minutes} minutes`); }
      else if (type === "settings") { await api.put("/pa/profile", { assistant_name: f.assistant_name, personality: f.personality }); toast.success("Assistant updated"); }
      done();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const TITLES = { event: "Add Event", reminder: "Create Reminder", note: "Add Note", booking: "New Booking Request", travel: "Add Trip", minutes: "Buy Extra Minutes", settings: "Assistant Settings", calendar: "Your Calendar", call: "Call a Contact" };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} />
      <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.3 }}
        data-testid={`pa-modal-${type}`} className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-lg pa-glass p-6" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="pa-display text-2xl">{TITLES[type]}</h3>
          <button data-testid="pa-modal-close" onClick={onClose} className="pa-plate p-1.5"><X className="h-4 w-4" strokeWidth={1.75} /></button>
        </div>

        {type === "event" && <div className="space-y-3">
          <In tid="pa-event-title" ph="Event title" v={f.title} on={(v) => set("title", v)} />
          <div className="grid grid-cols-2 gap-3"><In tid="pa-event-date" type="date" v={f.date} on={(v) => set("date", v)} /><In tid="pa-event-time" type="time" v={f.time} on={(v) => set("time", v)} /></div>
          <In tid="pa-event-loc" ph="Location (optional)" v={f.location} on={(v) => set("location", v)} />
        </div>}
        {type === "reminder" && <div className="space-y-3">
          <In tid="pa-reminder-text" ph="Remind me to…" v={f.text} on={(v) => set("text", v)} />
          <In tid="pa-reminder-due" type="date" v={f.due} on={(v) => set("due", v)} />
        </div>}
        {type === "note" && <Area tid="pa-note-text" ph="Type your note…" v={f.text} on={(v) => set("text", v)} />}
        {type === "booking" && <div className="space-y-3">
          <In tid="pa-booking-name" ph="Requester name" v={f.requester_name} on={(v) => set("requester_name", v)} />
          <In tid="pa-booking-title" ph="What is it for? (e.g. DJ set)" v={f.title} on={(v) => set("title", v)} />
          <div className="grid grid-cols-2 gap-3"><In tid="pa-booking-date" type="date" v={f.date} on={(v) => set("date", v)} /><In tid="pa-booking-fee" ph="Fee / budget" v={f.fee} on={(v) => set("fee", v)} /></div>
          <In tid="pa-booking-loc" ph="Location" v={f.location} on={(v) => set("location", v)} />
        </div>}
        {type === "travel" && <div className="space-y-3">
          <In tid="pa-travel-title" ph="Trip name" v={f.title} on={(v) => set("title", v)} />
          <div className="grid grid-cols-2 gap-3"><In tid="pa-travel-from" ph="From" v={f.origin} on={(v) => set("origin", v)} /><In tid="pa-travel-to" ph="To" v={f.destination} on={(v) => set("destination", v)} /></div>
          <In tid="pa-travel-depart" type="date" v={f.depart} on={(v) => set("depart", v)} />
        </div>}
        {type === "minutes" && <div className="space-y-3">
          <p className="pa-muted text-sm">£15 per 100 extra AI minutes.</p>
          <In tid="pa-minutes-bundles" type="number" ph="Number of 100-minute bundles" v={f.bundles} on={(v) => set("bundles", v)} />
          <p className="pa-gold text-sm">Total: £{15 * (Number(f.bundles) || 1)} · {100 * (Number(f.bundles) || 1)} minutes</p>
        </div>}
        {type === "settings" && <div className="space-y-3">
          <In tid="pa-settings-name" ph="Assistant name" v={f.assistant_name ?? meta.assistant_name} on={(v) => set("assistant_name", v)} />
          <select data-testid="pa-settings-personality" className="pa-input w-full px-4 py-3" value={f.personality || "professional"} onChange={(e) => set("personality", e.target.value)}>
            {["professional", "friendly", "luxury", "casual", "energetic"].map((p) => <option key={p} value={p} style={{ color: "#0A0A0A" }}>{p[0].toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>}
        {type === "call" && <div className="space-y-3">
          <In tid="pa-call-number" ph="Phone number" v={f.number} on={(v) => set("number", v)} />
          {f.number && <a data-testid="pa-call-link" href={`tel:${f.number}`} className="pa-btn-gold block text-center py-3 font-semibold">Call {f.number}</a>}
        </div>}
        {type === "calendar" && <div className="space-y-2">
          {(data.upcoming_events || []).length === 0 ? <Empty text="No upcoming events yet." /> : (data.upcoming_events || []).map((e) => <EventRow key={e.id} e={e} />)}
        </div>}

        {!["calendar", "call"].includes(type) && (
          <button data-testid="pa-modal-submit" onClick={submit} disabled={busy} className="pa-btn-gold w-full py-3 mt-6 font-semibold flex items-center justify-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        )}
      </motion.div>
    </>
  );
}
const In = ({ tid, ph, v, on, type = "text" }) => <input data-testid={tid} type={type} placeholder={ph} value={v || ""} onChange={(e) => on(e.target.value)} className="pa-input w-full px-4 py-3" />;
const Area = ({ tid, ph, v, on }) => <textarea data-testid={tid} placeholder={ph} value={v || ""} onChange={(e) => on(e.target.value)} rows={4} className="pa-input w-full px-4 py-3" />;
